import { ethers, BigNumber } from "ethers";
import { logger } from "../../../utils/logger";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "./relayerResponse";
import { fromPerpMarginMode } from "../../../utils/margin";
import { getAgentWallet, ensurePerpAgentApproved } from "../../../utils/oneClick";
import { formatGaslessRelayError } from "./usePerpOrder.helpers";
import { orderRejected } from "./orderTransactionError";
import { IPerpOrderParams } from "./usePerpOrder.types";
import { TradeExecutedDetail } from "../../../utils/tradeEvents";

interface GaslessPerpBaseArgs {
  account: string;
  masterAccount: string | null | undefined;
  params: IPerpOrderParams;
  rawPrice: BigNumber;
  rawSize: BigNumber;
  isLong: boolean;
  orderType: number;
  marginMode: number;
  acceptablePrice: BigNumber;
  chainId: number;
  perpDomainName: string;
  gaslessPerpRouter: string;
  relayerUrl: string;
  perpEngine?: string;
  agentRegistry?: string;
  signer: ethers.providers.JsonRpcSigner;
  revalidatePerpDataSoon: (detail?: TradeExecutedDetail) => void;
}

// Resolve the signing key for a gasless perp order. Heals a one-click session that predates the
// AgentRegistry becoming authoritative — re-approve the stored agent via the registry (one popup,
// relayed) before it would fail "Unauthorized signer". Falls back to null (→ the connected wallet
// signs, a popup per order) when there's no usable/approved agent or the heal fails.
export const resolveGaslessPerpAgent = async (args: {
  account: string;
  masterAccount: string | null | undefined;
  chainId: number;
  perpEngine?: string;
  gaslessPerpRouter: string;
  agentRegistry?: string;
  perpDomainName: string;
  relayer: string;
  signer: ethers.providers.JsonRpcSigner;
}): Promise<ethers.Wallet | null> => {
  const owner = args.masterAccount || args.account;
  const agentWallet = getAgentWallet(owner, args.chainId);
  // `isApprovedAgent` lives on the agent-aware GASLESS router (== the consolidated PerpEngine on
  // Monad, a dedicated gasless CLOBPerpetualRouter on modular chains like Arc). The regular
  // perpRouter does NOT implement it and reverts the check — which used to force a per-order wallet
  // popup on Arc even with one-click enabled. Always verify against the gasless router.
  const authEngine = args.gaslessPerpRouter || args.perpEngine;
  if (!agentWallet || !authEngine) return agentWallet;
  try {
    const ok = await ensurePerpAgentApproved({
      provider: args.signer.provider as any,
      account: owner,
      chainId: args.chainId,
      perpEngine: authEngine,
      agentRegistry: args.agentRegistry,
      perpDomainName: args.perpDomainName,
      relayerUrl: args.relayer,
    });
    return ok ? agentWallet : null;
  } catch {
    return null; // heal failed — fall back to the connected wallet
  }
};

interface GaslessOpenArgs extends GaslessPerpBaseArgs {
  leverage: number;
}

// ── Gasless path: sign EIP-712 PlacePerpOrder + relay it (user pays NO gas) ──
// The connected wallet signs the order; the relayer (relayerUrl) submits
// openPositionBySignature and pays gas. The on-chain PerpEngine verifies
// signer == trader, so the signing domain MUST match the engine exactly:
// name "PerpEngine", version "1", chainId, verifyingContract = the engine.
// reduceOnly is handled by its own gasless branch below (placeReduceOnlyPerpOrder).
export const relayGaslessOpen = async ({
  account,
  masterAccount,
  params,
  rawPrice,
  rawSize,
  isLong,
  orderType,
  leverage,
  marginMode,
  acceptablePrice,
  chainId,
  perpDomainName,
  gaslessPerpRouter,
  relayerUrl,
  perpEngine,
  agentRegistry,
  signer,
  revalidatePerpDataSoon,
}: GaslessOpenArgs): Promise<{ order_id: string; tx_hash: string }> => {
  const ZERO = ethers.constants.AddressZero;
  const relayer = relayerUrl.replace(/\/+$/, "");
  const nonceRes = await fetch(`${relayer}/nonce/${account}?router=perp`);
  const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
  const deadline = String(Math.floor(Date.now() / 1000) + 3600);
  const side = isLong ? 0 : 1;
  const domain = {
    name: perpDomainName,
    version: "1",
    chainId,
    verifyingContract: gaslessPerpRouter,
  };
  const types = {
    PlacePerpOrder: [
      { name: "user", type: "address" },
      { name: "market", type: "address" },
      { name: "price", type: "uint128" },
      { name: "size", type: "uint128" },
      { name: "collateralAmount", type: "uint128" },
      { name: "side", type: "uint8" },
      { name: "orderType", type: "uint8" },
      { name: "timeInForce", type: "uint8" },
      { name: "leverage", type: "uint16" },
      { name: "marginMode", type: "uint8" },
      { name: "acceptablePrice", type: "uint256" },
      { name: "builder", type: "address" },
      { name: "builderFee", type: "uint16" },
      { name: "nonce", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
  };
  const message = {
    user: account,
    market: params.market,
    price: rawPrice,
    size: rawSize,
    collateralAmount: 0,
    side,
    orderType,
    timeInForce: 0, // GTC — contract forces this into the hash
    leverage,
    marginMode,
    acceptablePrice,
    builder: ZERO,
    builderFee: 0,
    nonce,
    deadline,
  };
  // One-click mode: sign with the stored agent session key — NO wallet popup. The order's
  // `user` is still the main account, and the on-chain PerpEngine authorizes the agent via
  // _isAuthorizedFor(account, agent). When one-click is off (or expired), the connected
  // wallet signs each order (a popup per order — the "without one-click" mode).
  // Sign with the MASTER's agent — when `account` is a subaccount, the order's trader is the
  // subaccount but the on-chain PerpEngine authorizes the master (or master's agent).
  const agentWallet = await resolveGaslessPerpAgent({
    account, masterAccount, chainId, perpEngine, gaslessPerpRouter, agentRegistry, perpDomainName, relayer, signer,
  });
  const signature = agentWallet
    ? await agentWallet._signTypedData(domain, types, message)
    : await signer._signTypedData(domain, types, message);
  const relayRes = await fetch(`${relayer}/relay`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      action: "openPerpPosition",
      params: {
        user: account,
        market: params.market,
        price: rawPrice.toString(),
        size: rawSize.toString(),
        isLong,
        marginMode,
        leverage,
        orderType,
        acceptablePrice: acceptablePrice.toString(),
        builder: ZERO,
        builderFee: 0,
        nonce,
        deadline,
      },
      signature,
    }),
  });
  const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
  if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
    throw orderRejected(formatGaslessRelayError(relayJson?.error, "Gasless relay failed. Please try again.", relayJson?.reverted));
  }
  logger.debug(`[PerpOrder][gasless] relayed: tx=${relayJson.txHash}`);
  revalidatePerpDataSoon({
    openedPosition: {
      instrument_id: params.market,
      side: params.side,
      // See the note in usePerpOrder.place: without the margin mode this
      // releases the other leg pending close too.
      margin_type: fromPerpMarginMode(params.marginMode),
    },
  });
  return {
    order_id: relayJson.orderId?.toString() || relayJson.txHash,
    tx_hash: relayJson.txHash,
  };
};

// ── Gasless reduce-only path: sign EIP-712 PlaceReduceOnlyPerpOrder + relay it ──
// A reduce-only order can only ever shrink an existing opposite-side position — it can never
// open exposure or move funds — so it is authorized by the same agent/subaccount predicate as
// opens. A DISTINCT typehash (PlaceReduceOnlyPerpOrder) prevents replaying it as an open order.
export const relayGaslessReduceOnly = async ({
  account,
  masterAccount,
  params,
  rawPrice,
  rawSize,
  isLong,
  orderType,
  marginMode,
  acceptablePrice,
  chainId,
  perpDomainName,
  gaslessPerpRouter,
  relayerUrl,
  perpEngine,
  agentRegistry,
  signer,
  revalidatePerpDataSoon,
}: GaslessPerpBaseArgs): Promise<{ order_id: string; tx_hash: string }> => {
  const relayer = relayerUrl.replace(/\/+$/, "");
  const nonceRes = await fetch(`${relayer}/nonce/${account}?router=perp`);
  const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
  const deadline = String(Math.floor(Date.now() / 1000) + 3600);
  const domain = {
    name: perpDomainName,
    version: "1",
    chainId,
    verifyingContract: gaslessPerpRouter,
  };
  const types = {
    PlaceReduceOnlyPerpOrder: [
      { name: "trader", type: "address" },
      { name: "market", type: "address" },
      { name: "size", type: "uint128" },
      { name: "price", type: "uint128" },
      { name: "isLong", type: "bool" },
      { name: "orderType", type: "uint8" },
      { name: "marginMode", type: "uint8" },
      { name: "acceptablePrice", type: "uint256" },
      { name: "nonce", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
  };
  const message = {
    trader: account,
    market: params.market,
    size: rawSize,
    price: rawPrice,
    isLong,
    orderType,
    marginMode,
    acceptablePrice,
    nonce,
    deadline,
  };
  // Agent session key signs silently when one-click is active; otherwise the wallet signs once.
  // Sign with the MASTER's agent — when `account` is a subaccount, the order's trader is the
  // subaccount but the on-chain PerpEngine authorizes the master (or master's agent).
  const agentWallet = await resolveGaslessPerpAgent({
    account, masterAccount, chainId, perpEngine, gaslessPerpRouter, agentRegistry, perpDomainName, relayer, signer,
  });
  const signature = agentWallet
    ? await agentWallet._signTypedData(domain, types, message)
    : await signer._signTypedData(domain, types, message);
  const relayRes = await fetch(`${relayer}/relay`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      action: "placeReduceOnlyPerpOrder",
      params: {
        trader: account,
        market: params.market,
        size: rawSize.toString(),
        price: rawPrice.toString(),
        isLong,
        marginMode,
        orderType,
        acceptablePrice: acceptablePrice.toString(),
        nonce,
        deadline,
      },
      signature,
    }),
  });
  const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
  if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
    throw orderRejected(formatGaslessRelayError(relayJson?.error, "Gasless relay failed. Please try again.", relayJson?.reverted));
  }
  logger.debug(`[PerpOrder][gasless][reduceOnly] relayed: tx=${relayJson.txHash}`);
  // No `openedPosition` — see the reduce-only branch of `usePerpOrder.place`:
  // the order side is the opposite of the position being reduced, so reporting
  // it as an open released the pending close of a genuine position on that
  // market and side.
  revalidatePerpDataSoon();
  return {
    order_id: relayJson.orderId?.toString() || relayJson.txHash,
    tx_hash: relayJson.txHash,
  };
};
